import { useMemo, useState } from 'react';

import {
  IPortfolioPerformanceAccountReport,
  IPortfolioPerformanceAccounts,
} from '../../../../../providers/portfolio-performance';
import {
  buildWindowedReturns,
  ComparisonSeriesInput,
} from '../../../../../providers/portfolio-performance/build-indexed-comparison';
import {
  buildCorrelationMatrix,
  pairedReturns,
  MIN_OVERLAP,
} from '../../../../../providers/portfolio-performance/build-correlation-matrix';
import { olsFit } from '../../../../../providers/portfolio-performance/ols-fit';
import { isReferenceStrategyAccount } from '../../../../../providers/portfolio-performance/reference-strategies/reference-strategy.constants';
import { useThemeMode } from '../../../../../providers/theme-provider';
import { getTokenValue } from '../../../../../styles/tokens';
import Chart from '../../../../../components/v2/chart';
import RadioButtonList from '../../../../../components/radio-button-list';
import { RangeListEnum } from '../../../types';
import { EXTENDED_RANGES, ExtendedRange } from '../../../range';

interface CorrelationScatterTabProps {
  // Same shared row selection the Indexed tab reads (real strategies AND
  // Buy-and-Hold references, treated identically). < 2 => the compare prompt.
  accounts: IPortfolioPerformanceAccounts[];
  reportsByAccount: Record<string, IPortfolioPerformanceAccountReport>;
  isNamesBlurred?: boolean;
}

// Opens on 3M by default — matches the Indexed tab so switching tabs keeps a
// consistent window feel.
const DEFAULT_RANGE: ExtendedRange = RangeListEnum.M3;

// A correlation cell's background: red at -1, neutral at 0, green at +1, alpha
// scaled by |r| so strong correlations read at a glance. Theme-agnostic rgba.
const cellColor = (r: number | null): string => {
  if (r === null) return 'transparent';
  const alpha = Math.min(0.75, Math.abs(r) * 0.75);
  const rgb = r >= 0 ? '46, 125, 50' : '198, 40, 40';
  return `rgba(${rgb}, ${alpha})`;
};

const formatPct = (value: number): string => `${(value * 100).toFixed(1)}%`;

export const CorrelationScatterTab = ({
  accounts,
  reportsByAccount,
  isNamesBlurred = false,
}: CorrelationScatterTabProps) => {
  const { resolvedMode } = useThemeMode();
  const [rangeSelected, setRangeSelected] = useState<ExtendedRange>(DEFAULT_RANGE);
  // The clicked matrix cell [rowIndex, colIndex]; null => default to the first
  // off-diagonal pair for whatever selection is current.
  const [selectedCell, setSelectedCell] = useState<[number, number] | null>(null);

  const seriesInputs = useMemo<ComparisonSeriesInput[]>(
    () =>
      accounts.map((account) => ({
        key: account.name,
        label: account.name,
        color: '',
        historical: reportsByAccount[account.name]?.historical,
      })),
    [accounts, reportsByAccount],
  );

  const windowed = useMemo(
    () => buildWindowedReturns(seriesInputs, rangeSelected),
    [seriesInputs, rangeSelected],
  );

  const matrix = useMemo(() => buildCorrelationMatrix(windowed), [windowed]);

  const labelOf = (key: string) =>
    isNamesBlurred && !isReferenceStrategyAccount(key) ? '••••••' : key;

  const n = windowed.length;

  // Guard: fewer than two selected series => nothing to correlate.
  if (n < 2) {
    return (
      <div className="correlation-scatter-tab">
        <div className="no-data-metrics">
          <span>Select at least two rows above to compare.</span>
        </div>
      </div>
    );
  }

  // Resolve the active cell: clamp a stale click to the current selection, else
  // default to the first off-diagonal pair (row 0, col 1).
  const activeCell: [number, number] =
    selectedCell && selectedCell[0] < n && selectedCell[1] < n && selectedCell[0] !== selectedCell[1]
      ? selectedCell
      : [0, 1];
  const [rowIdx, colIdx] = activeCell;

  const colSeries = windowed[colIdx];
  const rowSeries = windowed[rowIdx];
  const { xs, ys } = pairedReturns(colSeries, rowSeries);
  const pairCell = matrix.cells[rowIdx][colIdx];
  const fit = olsFit(xs, ys);
  const enoughPoints = xs.length >= MIN_OVERLAP && fit !== null;

  const pointColor = getTokenValue('charts.turquoise', resolvedMode);
  const lineColor = getTokenValue('charts.orange', resolvedMode);
  // Zero-crosshair: a clearer line on the x=0 and y=0 axes than the gridlines.
  const zeroLineColor = resolvedMode === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)';

  // Cheap to rebuild each render; kept out of useMemo so no hook runs after the
  // < 2-series early return above (Rules of Hooks).
  const scatterSeries =
    enoughPoints && fit
      ? [
          {
            type: 'scatter' as const,
            data: xs.map((x, i) => [x, ys[i]]),
            symbolSize: 7,
            color: pointColor,
            smooth: false,
            markLine: {
              silent: true,
              symbol: 'none',
              label: { show: false },
              lineStyle: { color: zeroLineColor, type: 'solid', width: 1, opacity: 0.8 },
              data: [{ xAxis: 0 }, { yAxis: 0 }],
            },
          },
          {
            type: 'line' as const,
            data: [
              [Math.min(...xs), fit.slope * Math.min(...xs) + fit.intercept],
              [Math.max(...xs), fit.slope * Math.max(...xs) + fit.intercept],
            ],
            color: lineColor,
            showSymbol: false,
            lineStyle: { width: 2 },
            smooth: false,
          },
        ]
      : [];

  // nameGap pushes the axis TITLE clear of the % tick labels (the y title needs
  // more room than the x, since vertical tick labels are wider).
  const axisBase = (name: string, nameGap: number) => ({
    type: 'value' as const,
    name,
    nameLocation: 'center' as const,
    nameGap,
    axisLabel: { formatter: (v: number) => formatPct(v) },
  });

  const scatterTooltip = {
    trigger: 'item' as const,
    formatter: (params: any) => {
      const [x, y] = params.data as [number, number];
      return `${labelOf(colSeries.key)}: ${formatPct(x)}<br/>${labelOf(rowSeries.key)}: ${formatPct(y)}`;
    },
  };

  return (
    <div className="correlation-scatter-tab">
      <div className="comparison-controls">
        <div className="comparison-subtitle">
          Pearson correlation of selected series&apos; daily returns over the window; click a cell to
          plot its pair.
        </div>
        <RadioButtonList
          options={EXTENDED_RANGES}
          defaultValue={rangeSelected}
          dividerBefore={['MTD']}
          onChange={(range) => setRangeSelected(range as ExtendedRange)}
        />
      </div>

      <div className="correlation-panes">
        <div className="correlation-matrix-pane">
          <table className="correlation-matrix">
            <thead>
              <tr>
                <th className="corner" />
                {matrix.keys.map((key) => (
                  <th key={key} className="col-head" title={labelOf(key)}>
                    <span className="col-head-label">{labelOf(key)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.cells.map((row, i) => (
                <tr key={matrix.keys[i]}>
                  <th className="row-head" title={labelOf(matrix.keys[i])}>
                    {labelOf(matrix.keys[i])}
                  </th>
                  {row.map((cell, j) => {
                    const isDiagonal = i === j;
                    const isSelected = i === rowIdx && j === colIdx;
                    const text = isDiagonal
                      ? '100.00%'
                      : cell.r === null
                        ? 'n/a'
                        : `${(cell.r * 100).toFixed(2)}%`;
                    return (
                      <td
                        key={matrix.keys[j]}
                        className={`matrix-cell${isDiagonal ? ' diagonal' : ''}${
                          isSelected ? ' selected' : ''
                        }`}
                        style={{ backgroundColor: isDiagonal ? 'transparent' : cellColor(cell.r) }}
                      >
                        {isDiagonal ? (
                          <span>{text}</span>
                        ) : (
                          <button
                            type="button"
                            className="matrix-cell-button"
                            onClick={() => setSelectedCell([i, j])}
                            aria-label={`Correlation of ${labelOf(matrix.keys[i])} and ${labelOf(
                              matrix.keys[j],
                            )}`}
                            aria-pressed={isSelected}
                          >
                            {text}
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="correlation-scatter-pane">
          <div className="scatter-caption">
            <span className="scatter-pair">
              {labelOf(rowSeries.key)} <span className="vs">vs</span> {labelOf(colSeries.key)}
            </span>
            {enoughPoints && fit && pairCell.r !== null ? (
              <span className="scatter-stats">
                r = {pairCell.r.toFixed(2)} · R² = {fit.r2.toFixed(2)} · slope ={' '}
                {fit.slope.toFixed(2)} · n = {xs.length}
              </span>
            ) : enoughPoints && fit ? (
              <span className="scatter-stats">
                No variation in {labelOf(rowSeries.key)} over this window · n = {xs.length}
              </span>
            ) : null}
          </div>
          {enoughPoints ? (
            <div className="scatter-chart">
              <Chart
                key={`${rowIdx}-${colIdx}-${rangeSelected}`}
                series={scatterSeries}
                xAxis={axisBase(labelOf(colSeries.key), 34)}
                yAxis={axisBase(labelOf(rowSeries.key), 58)}
                tooltip={scatterTooltip}
                grid={{ left: 64, right: 28, top: 16, bottom: 60, containLabel: true }}
              />
            </div>
          ) : (
            <div className="no-data-metrics">
              <span>
                {xs.length < MIN_OVERLAP
                  ? `Not enough overlapping days between this pair to plot (${xs.length} shared ${
                      xs.length === 1 ? 'day' : 'days'
                    }).`
                  : `No variation in ${labelOf(colSeries.key)} over this window — can't fit a line.`}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
